'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Sparkles, FileCode, FileText, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsonToXmlContent } from '../../data/json-to-xml-content';
import Link from 'next/link';

const sampleJson = `{
  "person": {
    "name": "John Doe",
    "age": 30,
    "isActive": true,
    "address": {
      "street": "123 Main St",
      "city": "New York",
      "country": "USA"
    },
    "emails": ["john@example.com", "johndoe@work.com"],
    "phoneNumbers": [
      { "type": "home", "number": "555-1234" },
      { "type": "work", "number": "555-5678" }
    ]
  }
}`;

const sampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<root>
  <person>
    <name>John Doe</name>
    <age>30</age>
    <isActive>true</isActive>
    <address>
      <street>123 Main St</street>
      <city>New York</city>
      <country>USA</country>
    </address>
    <emails>john@example.com</emails>
    <emails>johndoe@work.com</emails>
    <phoneNumbers>
      <type>home</type>
      <number>555-1234</number>
    </phoneNumbers>
    <phoneNumbers>
      <type>work</type>
      <number>555-5678</number>
    </phoneNumbers>
  </person>
</root>`;

function formatXml(xml: string): string {
    const PADDING = '  ';
    const reg = /(>)(<)(\/*)/g;
    let pad = 0;

    // Add newlines and proper indentation
    xml = xml.replace(reg, '$1\r\n$2$3');

    return xml.split('\r\n').map((node) => {
        let indent = 0;
        if (node.match(/.+<\/\w[^>]*>$/)) {
            indent = 0;
        } else if (node.match(/^<\w[^>]*>[^<]*$/)) {
            indent = 1;
        } else if (node.match(/^<\/\w/)) {
            if (pad > 0) pad -= 1;
            indent = 0;
        } else if (node.match(/^<\w[^>]*[^/]>.*$/)) {
            indent = 1;
        } else {
            indent = 0;
        }

        const padding = PADDING.repeat(pad);
        pad += indent;

        return padding + node;
    }).join('\r\n');
}

function jsonToXml(json: any, rootName = 'root'): string {
    let xml = '';

    // Handle different data types
    if (json === null || json === undefined) {
        return '';
    } else if (typeof json === 'string') {
        return json.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    } else if (typeof json === 'number' || typeof json === 'boolean') {
        return String(json);
    }

    // Handle arrays
    if (Array.isArray(json)) {
        for (let i = 0; i < json.length; i++) {
            const item = json[i];
            const itemXml = jsonToXml(item, 'item');
            xml += `<item>${itemXml}</item>`;
        }
        return xml;
    }

    // Handle objects
    for (const key in json) {
        if (json.hasOwnProperty(key)) {
            const value = json[key];
            const validKey = key.replace(/[^\w\d_-]/g, '_').replace(/^[^a-zA-Z_]/, '_');

            if (value === null || value === undefined) {
                xml += `<${validKey} type="null"/>`;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    xml += `<${validKey} type="array"></${validKey}>`;
                } else {
                    xml += `<${validKey} type="array">`;
                    for (const item of value) {
                        if (typeof item === 'object' && item !== null) {
                            xml += jsonToXml(item, 'item');
                        } else {
                            xml += `<item>${jsonToXml(item)}</item>`;
                        }
                    }
                    xml += `</${validKey}>`;
                }
            } else if (typeof value === 'object') {
                xml += `<${validKey}>${jsonToXml(value)}</${validKey}>`;
            } else {
                const type = typeof value;
                const escapedValue = String(value)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;');
                xml += `<${validKey} type="${type}">${escapedValue}</${validKey}>`;
            }
        }
    }

    return rootName ? `<${rootName}>${xml}</${rootName}>` : xml;
}

export default function JsonToXmlTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isPretty, setIsPretty] = useState(true);
    const { toast } = useToast();

    const convertJsonToXml = (jsonString: string) => {
        try {
            if (!jsonString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            const jsonObj = JSON.parse(jsonString);
            let xml = jsonToXml(jsonObj);

            // Add XML declaration
            xml = '<?xml version="1.0" encoding="UTF-8"?>\n' + xml;

            // Format XML if pretty print is enabled
            if (isPretty) {
                xml = formatXml(xml);
            }

            setOutput(xml);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid JSON'}`);
            setOutput('');
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            convertJsonToXml(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input, isPretty]);

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The XML has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
    };

    const loadSample = () => {
        setInput(sampleJson);
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="JSON to XML Converter">
                Convert JSON data to XML format instantly with our free online converter. Perfect for API development, data migration, and format conversion.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        JSON Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Paste your JSON data here
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadSample}
                                        className="gap-1.5"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your JSON here...\n{\n  "example": {\n    "name": "value",\n    "items": [1, 2, 3]\n  }\n}'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        XML Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Your converted XML will appear here
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setIsPretty(!isPretty)}
                                        className={cn(
                                            'gap-1.5',
                                            isPretty ? 'bg-primary/5' : ''
                                        )}
                                    >
                                        {isPretty ? (
                                            <FileText className="h-3.5 w-3.5" />
                                        ) : (
                                            <FileCode className="h-3.5 w-3.5" />
                                        )}
                                        {isPretty ? 'Pretty' : 'Minified'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70'
                                    )}
                                    placeholder="Your converted XML will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your converted XML will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={jsonToXmlContent.title}
                    description={jsonToXmlContent.description}
                    content={jsonToXmlContent.content}
                    faqs={jsonToXmlContent.faqs}
                    relatedTools={jsonToXmlContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
